import { axiosInstance } from './http';
import { localNextDayRange } from '../pages/frontdesk/frontDesk';
import type {
  BookingContactRow,
  BookingHistoryEntry,
  FrontDeskCounts,
  FrontDeskOverview,
  OfflinePaymentMethod,
  PaymentSummary,
} from '../pages/bookings/frontDeskTypes';

export type BookingConsoleListRow = {
  id: string;
  businessId: string;
  businessName: string | null;
  serviceId: string;
  serviceName: string | null;
  userId: string;
  userName: string | null;
  userPhone: string | null;
  userEmail: string | null;
  staffId: string | null;
  staffName: string | null;
  spaceId: string | null;
  resourceId: string | null;
  branchId: string | null;
  branchName: string | null;
  startTime: string;
  endTime: string;
  status: string;
  totalPaid: number;
  /** Compact payment discriminator (backend `deriveConsolePaymentStatus`):
   *  PAID = money received · PENDING = online checkout started, not yet paid
   *  · BRANCH = pay-at-branch/cash (0 is by-design) · NONE = no payment. */
  paymentStatus: "PAID" | "PENDING" | "BRANCH" | "NONE";
  /** Same summary as the detail (C3 §7); absent on older backends. */
  paymentSummary?: { price: number | null; paid: number; balance: number | null };
  /** An online checkout was started for this booking. */
  onlineCheckout?: boolean;
  /** Set when this booking is a SESSIONS cohort enrollment (vs a one-off
   *  appointment). Drives the "Анги" label + lets the calendar drop the
   *  one-off event in favour of the cohort's occurrence events. */
  sessionId: string | null;
  cohortName: string | null;
  /** Booked with other services in one visit; absent on an older backend. */
  visitId?: string | null;
};

export async function listBookingsConsole(params: {
  page?: number;
  limit?: number;
  businessId?: string;
  branchId?: string;
  status?: string;
  serviceId?: string;
  from?: string;
  to?: string;
  resourceType?: 'staff' | 'space' | 'resource';
  resourceId?: string;
  staffIds?: string[];
  spaceIds?: string[];
  resourceIds?: string[];
}) {
  const response = await axiosInstance.get(`/booking/admin/list`, {
    params: {
      page: params.page ?? 1,
      limit: params.limit ?? 20,
      ...(params.businessId ? { businessId: params.businessId } : {}),
      ...(params.branchId ? { branchId: params.branchId } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...(params.serviceId ? { serviceId: params.serviceId } : {}),
      ...(params.from ? { from: params.from } : {}),
      ...(params.to ? { to: params.to } : {}),
      ...(params.resourceType
        ? {
            resourceType: params.resourceType,
            ...(params.resourceId ? { resourceId: params.resourceId } : {}),
          }
        : {}),
      ...(params.staffIds?.length ? { staffIds: params.staffIds.join(',') } : {}),
      ...(params.spaceIds?.length ? { spaceIds: params.spaceIds.join(',') } : {}),
      ...(params.resourceIds?.length ? { resourceIds: params.resourceIds.join(',') } : {}),
    },
  });
  return response.data as {
    list: BookingConsoleListRow[];
    total: number;
    page: number;
    limit: number;
  };
}

export async function listBookingsRangeConsole(params: {
  from: string;
  to: string;
  businessId?: string;
  branchId?: string;
  status?: string;
  resourceType?: 'staff' | 'space' | 'resource';
  resourceId?: string;
  staffIds?: string[];
  spaceIds?: string[];
  resourceIds?: string[];
  max?: number;
}) {
  const response = await axiosInstance.get(`/booking/admin/range`, {
    params: {
      from: params.from,
      to: params.to,
      ...(params.max ? { max: params.max } : {}),
      ...(params.businessId ? { businessId: params.businessId } : {}),
      ...(params.branchId ? { branchId: params.branchId } : {}),
      ...(params.status ? { status: params.status } : {}),
      ...(params.resourceType
        ? {
            resourceType: params.resourceType,
            ...(params.resourceId ? { resourceId: params.resourceId } : {}),
          }
        : {}),
      ...(params.staffIds?.length ? { staffIds: params.staffIds.join(',') } : {}),
      ...(params.spaceIds?.length ? { spaceIds: params.spaceIds.join(',') } : {}),
      ...(params.resourceIds?.length ? { resourceIds: params.resourceIds.join(',') } : {}),
    },
  });
  return response.data as {
    list: BookingConsoleListRow[];
    maxRows: number;
    truncated: boolean;
  };
}

export async function getBookingConsoleDetail(bookingId: string) {
  const response = await axiosInstance.get(`/booking/admin/${bookingId}`);
  return response.data as {
    booking: Record<string, unknown> & {
      id?: string;
      businessId?: string;
      startTime?: string;
      endTime?: string;
      status?: string;
      totalPaid?: number;
      settings?: Record<string, unknown> | null;
      business?: { name?: string };
      service?: {
        name?: string;
        duration?: number;
        // SERVICE_ONLY hides resource breakdown; RESOURCE_BASED shows it.
        settings?: {
          bookingMode?: "RESOURCE_BASED" | "SERVICE_ONLY";
          scheduling?: {
            mode?: "TIME_SLOTS" | "FULL_DAY" | "FIXED_DAYS";
            durationUnit?: "MINUTES" | "DAYS";
            durationValue?: number;
            checkInTime?: string;
            checkOutTime?: string;
          } | null;
        } | null;
      };
      // Either a registered user (when userId is set) or guest fields.
      // The backend returns user relation + raw guest columns either way;
      // UI prefers `user` when populated, falls back to guest.
      userId?: string | null;
      user?: { name?: string; phone?: string | null; email?: string | null } | null;
      guestName?: string | null;
      guestPhone?: string | null;
      guestEmail?: string | null;
      staff?: { id?: string; firstName?: string | null; lastName?: string | null };
      space?: { id?: string; name?: string };
      resource?: { id?: string; name?: string };
      partySize?: number | null;
      // Cohort (SESSIONS) enrollment linkage — eager-loaded by the backend
      // findOne. Present only for cohort enrollments.
      sessionId?: string | null;
      serviceSession?: {
        cohortName?: string | null;
        startDate?: string | null;
        endDate?: string | null;
        weeklySchedule?: Array<{
          day: string;
          time: string;
          durationMinutes: number;
        }> | null;
      } | null;
    };
    payments: Array<{
      id: string;
      amount: number;
      type: string;
      currency: string;
      channel?: string | null;
      createdAt?: string;
    }>;
  };
}

export async function refundBooking(
  bookingId: string,
  params: { amount: number; currency?: string; reason?: string },
) {
  const response = await axiosInstance.post(`/booking/admin/${bookingId}/refund`, params);
  return response.data as { payment: { id: string; amount: number }; totalPaid: number };
}

export async function cancelBookingConsole(bookingId: string, reason: string) {
  const response = await axiosInstance.patch(`/booking/admin/${bookingId}/cancel`, { reason });
  return response.data;
}

// ─── Front desk (B1 endpoints) ───────────────────────────────────────────────

export type {
  BookingContactRow,
  BookingHistoryEntry,
  FrontDeskCounts,
  FrontDeskOverview,
  FrontDeskRow,
  OfflinePaymentMethod,
  PaymentSummary,
} from '../pages/bookings/frontDeskTypes';

/** "Төлбөр бүртгэх" — cash / transfer / POS taken at the desk. */
export async function recordBookingPayment(
  bookingId: string,
  body: { method: OfflinePaymentMethod; amount: number; note?: string },
) {
  const response = await axiosInstance.post(`/booking/admin/${bookingId}/payments`, body);
  return response.data as { paymentSummary: PaymentSummary };
}

/** "Хугацаа сунгах". */
export async function updateBookingHold(bookingId: string, payBy: string) {
  const response = await axiosInstance.patch(`/booking/admin/${bookingId}/hold`, { payBy });
  return response.data;
}

/** "Төлбөргүй батлах". */
export async function confirmBookingHold(bookingId: string) {
  const response = await axiosInstance.post(`/booking/admin/${bookingId}/confirm`);
  return response.data;
}

/** "Ирсэн" / "Ирээгүй". */
export async function markBookingAttendance(bookingId: string, outcome: 'COMPLETED' | 'NO_SHOW') {
  const response = await axiosInstance.post(`/booking/admin/${bookingId}/attendance`, { outcome });
  return response.data;
}

/** "Буцаасан" — staff returned a refund a cancel left owed. */
export async function settleBookingRefund(
  bookingId: string,
  body: { method: OfflinePaymentMethod; note?: string; amount?: number },
) {
  const response = await axiosInstance.post(`/booking/admin/${bookingId}/refund-owed/settle`, body);
  return response.data;
}

/** Front desk «📞 Залгасан». */
export async function markBookingCallHandled(bookingId: string) {
  const response = await axiosInstance.post(`/booking/admin/${bookingId}/call-handled`);
  return response.data;
}

/** «Шийдсэн» — a customer's change request handled without moving the time. */
export async function resolveBookingChangeRequest(bookingId: string) {
  const response = await axiosInstance.post(`/booking/admin/${bookingId}/change-request/resolve`);
  return response.data;
}

export async function getBookingHistory(bookingId: string): Promise<BookingHistoryEntry[]> {
  const response = await axiosInstance.get(`/booking/admin/${bookingId}/history`);
  return response.data as BookingHistoryEntry[];
}

export async function getFrontDesk(params: {
  businessId: string;
  from: string;
  to: string;
  /** Call list window — the next local day. */
  callFrom?: string;
  callTo?: string;
}): Promise<FrontDeskOverview> {
  const response = await axiosInstance.get('/booking/admin/front-desk', { params });
  return response.data as FrontDeskOverview;
}

export async function getFrontDeskCounts(businessId: string): Promise<FrontDeskCounts> {
  const response = await axiosInstance.get('/booking/admin/front-desk/counts', {
    params: { businessId, ...localNextDayRange(new Date()) },
  });
  return response.data as FrontDeskCounts;
}

export async function listBookingContacts(params: {
  businessId: string;
  search: string;
  limit?: number;
}): Promise<{ list: BookingContactRow[]; total: number }> {
  const response = await axiosInstance.get('/booking/admin/contacts', {
    params: { businessId: params.businessId, search: params.search, limit: params.limit ?? 10 },
  });
  return response.data as { list: BookingContactRow[]; total: number };
}
